import { useCallback, useEffect, useState } from "react";
import { readPreference, writePreference } from "./usePreference";

type ThemePreference = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

const STORAGE_KEY = "nacs-internal.theme";
const MEDIA = "(prefers-color-scheme: dark)";

function parsePreference(raw: string): ThemePreference | null {
  return raw === "light" || raw === "dark" || raw === "system" ? raw : null;
}

export function resolveTheme(pref: ThemePreference, systemDark: boolean): ResolvedTheme {
  return pref === "system" ? (systemDark ? "dark" : "light") : pref;
}

function systemPrefersDark(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia(MEDIA).matches
  );
}

/** Stamps the resolved theme on <html>; tokens.css also honours prefers-color-scheme without JS. */
export function applyTheme(resolved: ResolvedTheme): void {
  document.documentElement.dataset["theme"] = resolved;
}

/** Called before first paint (main.tsx) so the page never flashes the wrong theme. */
export function applyStoredTheme(): void {
  const pref = readPreference(STORAGE_KEY, parsePreference) ?? "system";
  applyTheme(resolveTheme(pref, systemPrefersDark()));
}

export function useTheme() {
  const [preference, setPreferenceState] = useState<ThemePreference>(
    () => readPreference(STORAGE_KEY, parsePreference) ?? "system",
  );
  const [systemDark, setSystemDark] = useState(systemPrefersDark);

  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const mql = window.matchMedia(MEDIA);
    const onChange = (e: MediaQueryListEvent) => setSystemDark(e.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  const resolved = resolveTheme(preference, systemDark);
  useEffect(() => applyTheme(resolved), [resolved]);

  const setPreference = useCallback((next: ThemePreference) => {
    setPreferenceState(next);
    writePreference(STORAGE_KEY, next);
  }, []);

  const toggle = useCallback(
    () => setPreference(resolved === "dark" ? "light" : "dark"),
    [resolved, setPreference],
  );

  return { preference, resolved, setPreference, toggle };
}
